'use client';

import { ReactNode } from 'react';
import { toast as baseToast, ToastContent, ToastOptions } from 'react-toastify';
import ToastV4Body, { ToastV4Variant } from './ToastV4Body';

// Optional custom title via `data: { title }`. Otherwise the variant title is used.
const titleFrom = (options?: ToastOptions): ReactNode | undefined => {
  const data = options?.data;
  if (!data || typeof data !== 'object' || !('title' in data)) return undefined;
  const title = (data as {
    title?: ReactNode;
  }).title;
  return title || undefined;
};

// Wraps plain toast content (string / JSX) with the v4 title + message
// layout. Render-prop content is passed through untouched.
const wrap = (variant: ToastV4Variant, content: ToastContent, title?: ReactNode): ToastContent => {
  if (typeof content === 'function') {
    return content;
  }
  return <ToastV4Body variant={variant} title={title} data-sentry-element="ToastV4Body" data-sentry-component="wrap" data-sentry-source-file="toast.tsx">
            {content}
        </ToastV4Body>;
};

/**
 * Drop-in replacement for react-toastify's `toast` — identical API, but
 * every toast body is rendered with the v4 design (localized title per
 * type + the original message underneath). Import this instead of
 * `react-toastify` when firing toasts.
 */
export const toast: typeof baseToast = Object.assign(((content: ToastContent, options?: ToastOptions) => baseToast(wrap('info', content, titleFrom(options)), options)) as typeof baseToast, baseToast, {
  info: (content: ToastContent, options?: ToastOptions) => baseToast.info(wrap('info', content, titleFrom(options)), options),
  success: (content: ToastContent, options?: ToastOptions) => baseToast.success(wrap('success', content, titleFrom(options)), options),
  error: (content: ToastContent, options?: ToastOptions) => baseToast.error(wrap('error', content, titleFrom(options)), options),
  warning: (content: ToastContent, options?: ToastOptions) => baseToast.warning(wrap('warning', content, titleFrom(options)), options),
  warn: (content: ToastContent, options?: ToastOptions) => baseToast.warn(wrap('warning', content, titleFrom(options)), options)
});